import {
  ElementType,
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { NavLink, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { ClientContainer } from '@/providers';

type MenuItem = {
  title: string;
  url?: string;
  icon?: ElementType;
  pathMatch?: string;
  items?: {
    title: string;
    url: string;
  }[];
};

type PortalDropdownProps = {
  isOpen: boolean;
  position: { top: number; left: number };
  dropdownRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
};

type CollapsibleNavProps = {
  children: React.ReactNode;
  pathMatch: string;
};

type DropdownController = {
  isOpen: boolean;
  position: { top: number; left: number };
  triggerRef: React.RefObject<HTMLButtonElement>;
  dropdownRef: React.RefObject<HTMLDivElement>;
  openDropdown: () => void;
  closeDropdown: () => void;
};

const usePortalDropdown = (): DropdownController => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top,
        left: rect.right + 5,
      });
    }
  }, []);

  const openDropdown = useCallback(() => {
    updatePosition();
    setIsOpen(true);
  }, [updatePosition]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updatePosition);

    /* eslint-disable-next-line consistent-return */
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, closeDropdown, updatePosition]);

  return {
    isOpen,
    position,
    triggerRef,
    dropdownRef,
    openDropdown,
    closeDropdown,
  };
};

const useDropdownManager = (items: MenuItem[]) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const controller = usePortalDropdown();

  const toggleDropdown = useCallback(
    (title: string) => {
      if (activeDropdown === title) {
        controller.closeDropdown();
        setActiveDropdown(null);
      } else {
        if (activeDropdown) {
          controller.closeDropdown();
        }
        setActiveDropdown(title);
        controller.openDropdown();
      }
    },
    [activeDropdown, controller]
  );

  return {
    activeDropdown,
    isDropdownOpen: (title: string) => activeDropdown === title,
    toggleDropdown,
    controller,
  };
};

const PortalDropdown = ({
  isOpen,
  position,
  dropdownRef,
  children,
}: PortalDropdownProps) => {
  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50 fixed min-w-36 opacity-100"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      {children}
    </div>,
    document.body
  );
};

const CollapsibleNav = ({ children, pathMatch }: CollapsibleNavProps) => {
  const { state: clientState } = ClientContainer.useContainer();
  const clientId = clientState?.selectedClient?.id;
  const match = useMatch(`/ws/${clientId}${pathMatch}`);
  return (
    <Collapsible defaultOpen={!!match} className="group/collapsible" asChild>
      {children}
    </Collapsible>
  );
};

export function NavMain({ items: menuItems }: { items: MenuItem[] }) {
  const sidebar = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  // Use the improved dropdown manager
  const dropdownManager = useDropdownManager(menuItems);

  const { state: clientState } = ClientContainer.useContainer();

  // dymamically add selected client to menu items
  const menuItemsWithSelectedClient = menuItems.map(menuItem => ({
    ...menuItem,
    url: menuItem.url
      ? `/ws/${clientState?.selectedClient?.id}${menuItem.url}`
      : undefined,
    items: menuItem.items?.map(subItem => ({
      ...subItem,
      url: `/ws/${clientState?.selectedClient?.id}${subItem.url}`,
    })),
  }));

  return (
    <SidebarGroup>
      <SidebarMenu>
        {menuItemsWithSelectedClient.map(menuItem => {
          const menuClassname = cn(
            'data-[active=true]:border-sidebar-border data-[active=true]:shadow-[0px_4px_16px_-10px_#1D293924]',
            'data-[state=open]:text-sidebar-accent-foreground',
            !!menuItem.items?.length &&
              'data-[state=open]:hover:bg-transparent hover:bg-transparent data-[state=open]:hover:border-transparent hover:border-transparent',
            !sidebar.open &&
              'justify-center group-data-[collapsible=icon]:!size-10'
          );

          const isSubMenuActive =
            menuItem.items?.some(item =>
              location.pathname.includes(item.url)
            ) || false;

          const renderFullMenu = () => (
            <>
              {menuItem.icon && (
                <menuItem.icon
                  className={cn(
                    'group-hover/menu-button:text-primary',
                    'group-data-[active=true]/menu-button:text-primary',
                    'group-data-[submenu=true]/menu-button:text-primary'
                  )}
                />
              )}
              <span>{menuItem.title}</span>
              {menuItem.items && menuItem.items?.length > 0 && (
                <i className="ti ti-chevron-down ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180 text-grayscale-400" />
              )}
            </>
          );

          const renderCollapsedWithItems = () => (
            <>
              {menuItem.icon && (
                <menuItem.icon
                  className={cn(
                    'group-hover/menu-button:text-primary',
                    'group-data-[submenu=true]/menu-button:text-primary'
                  )}
                />
              )}
            </>
          );

          const renderCollapsedNoItems = () =>
            menuItem.icon && (
              <menuItem.icon
                className={cn(
                  'group-hover/menu-button:text-primary',
                  'group-data-[active=true]/menu-button:text-primary',
                  'group-data-[state=open]/menu-button:text-primary'
                )}
              />
            );

          const menuContent = (() => {
            if (sidebar.open) {
              return renderFullMenu();
            }
            if (menuItem.items && menuItem.items.length > 0) {
              return renderCollapsedWithItems();
            }
            return renderCollapsedNoItems();
          })();

          const hasSubmenu = menuItem.items && menuItem.items.length > 0;
          const isCurrentDropdownOpen = dropdownManager.isDropdownOpen(
            menuItem.title
          );

          // common menu button
          const baseButton = (
            <SidebarMenuButton
              ref={
                hasSubmenu && !sidebar.open
                  ? dropdownManager.controller.triggerRef
                  : null
              }
              isActive={menuItem.url ? undefined : undefined}
              isSubMenuActive={isSubMenuActive}
              tooltip={
                !hasSubmenu || !isCurrentDropdownOpen
                  ? menuItem.title
                  : undefined
              }
              className={menuClassname}
              onClick={() => {
                if (hasSubmenu && !sidebar.open) {
                  dropdownManager.toggleDropdown(menuItem.title);
                } else if (!hasSubmenu && dropdownManager.activeDropdown) {
                  dropdownManager.toggleDropdown(
                    dropdownManager.activeDropdown
                  );
                }
              }}
            >
              {menuContent}
            </SidebarMenuButton>
          );

          const menuButton = menuItem.url ? (
            <NavLink
              className="w-full"
              to={menuItem.url}
              onClick={() => {
                if (!hasSubmenu && dropdownManager.activeDropdown) {
                  dropdownManager.toggleDropdown(
                    dropdownManager.activeDropdown
                  );
                }
                if (sidebar.isMobile) {
                  sidebar.toggleSidebar();
                }
                if (sidebar.isTablet) {
                  sidebar.toggleSidebar();
                }
              }}
            >
              {({ isActive }) => (
                <SidebarMenuButton
                  isActive={isActive}
                  tooltip={menuItem.title}
                  className={menuClassname}
                >
                  {menuContent}
                </SidebarMenuButton>
              )}
            </NavLink>
          ) : (
            baseButton
          );

          // Create portal dropdown for collapsed sidebar items with submenu
          const portalDropdown = hasSubmenu &&
            !sidebar.open &&
            isCurrentDropdownOpen && (
              <PortalDropdown
                isOpen
                position={dropdownManager.controller.position}
                dropdownRef={dropdownManager.controller.dropdownRef}
              >
                {menuItem.items?.map((subItem, index) => (
                  <div
                    tabIndex={0}
                    role="menuitem"
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        navigate(subItem.url);
                        dropdownManager.toggleDropdown(menuItem.title);
                      }
                    }}
                    key={index}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      if (subItem.url) {
                        navigate(subItem.url);
                        dropdownManager.toggleDropdown(menuItem.title);
                      }
                    }}
                  >
                    {subItem.title}
                  </div>
                ))}
              </PortalDropdown>
            );

          // NOTE: no item to expand
          if (!menuItem.items?.length) {
            return (
              <SidebarMenuItem
                key={menuItem.title}
                className={cn(!sidebar.open && 'mx-auto')}
              >
                {menuButton}
              </SidebarMenuItem>
            );
          }

          return sidebar.open ? (
            <CollapsibleNav
              key={menuItem.title}
              pathMatch={menuItem.pathMatch || '#'}
            >
              <SidebarMenuItem className={cn(!sidebar.open && 'mx-auto')}>
                <CollapsibleTrigger asChild>{menuButton}</CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {menuItem.items?.map(subItem => (
                      <SidebarMenuItem
                        key={subItem.title}
                        onClick={() => {
                          if (sidebar.isMobile) {
                            sidebar.toggleSidebar();
                          }
                          if (sidebar.isTablet) {
                            sidebar.toggleSidebar();
                          }
                        }}
                      >
                        {subItem.url ? (
                          <NavLink className="w-full" to={subItem.url}>
                            {({ isActive }) => (
                              <SidebarMenuButton
                                isActive={isActive}
                                tooltip={menuItem.title}
                                className={cn(menuClassname, 'hover:bg-white')}
                              >
                                <span>{subItem.title}</span>
                              </SidebarMenuButton>
                            )}
                          </NavLink>
                        ) : (
                          <SidebarMenuButton
                            tooltip={menuItem.title}
                            className={menuClassname}
                          >
                            <span>{subItem.title}</span>
                          </SidebarMenuButton>
                        )}
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </CollapsibleNav>
          ) : (
            <SidebarMenuItem key={menuItem.title} className="mx-auto">
              {menuButton}
              {portalDropdown}
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
